/**
 * Rug Pull System (Sprint 2b) - recoverable rug events on meme coins.
 * After a pump ends (whale cycle or pump-ending scenario) there is a small
 * chance of a rug: 10~20s crash of -70~90%, 3~5 min at the floor, then a
 * slow mean-reverting recovery. Publishes an emergency news item and pays
 * out active rug insurance at crash start. Also keeps the rolling price
 * samples used by the panic-exit hook.
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { getCoinConfig, type CoinConfig } from '../config/coins.js';
import { NEWS_CONFIG } from '../config/coinNews.js';
import { INSURANCE_CONFIG, PANIC_CONFIG, RUG_CONFIG } from '../config/risk.js';
import type { FeedNewsItem } from '../infoState.js';
import type { PriceSample, RiskState, RugPullState } from '../riskState.js';

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Direct price override while a rug is crashing or at the floor. */
export function rugPriceOverride(
  rug: RugPullState | undefined,
  cfg: CoinConfig,
  prevPrice: number,
  now: number
): number | null {
  if (!rug) return null;
  if (rug.phase === 'crashing') {
    const span = rug.phaseEndsAt - rug.startedAt;
    const progress = span > 0 ? clamp((now - rug.startedAt) / span, 0, 1) : 1;
    const path = rug.startPrice * Math.pow(rug.targetPrice / rug.startPrice, progress);
    const noise = 1 + (Math.random() - 0.5) * 0.04;
    return Math.max(cfg.minPrice, path * noise);
  }
  if (rug.phase === 'floor') {
    const wander = prevPrice * (1 + (Math.random() - 0.5) * 2 * RUG_CONFIG.floorNoise);
    const low = Math.max(cfg.minPrice, rug.targetPrice * 0.85);
    return clamp(wander, low, Math.max(low, rug.targetPrice * 1.2));
  }
  return null;
}

/** Mild upward momentum push during the recovery phase. */
export function rugBias(rug: RugPullState | undefined, cfg: CoinConfig): number {
  return rug?.phase === 'recovering' ? RUG_CONFIG.recoveryBias * cfg.volatility : 0;
}

function makeId(now: number): string {
  return `${now}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Prepend the emergency "devs moving funds" news to the feed. */
function publishRugNews(state: GameState, coinId: string, now: number): GameState {
  if (!state.info) return state;
  const item: FeedNewsItem = {
    kind: 'news',
    instanceId: makeId(now),
    newsId: 'rug_alert',
    coinId,
    direction: -1,
    publishedAt: now,
  };
  return {
    ...state,
    info: { ...state.info, feed: [item, ...state.info.feed].slice(0, NEWS_CONFIG.feedLimit) },
  };
}

/** Pay out an active insurance policy at the pre-crash valuation (one-shot). */
function payInsurance(state: GameState, coinId: string, preCrashPrice: number, now: number): GameState {
  const policy = state.risk?.insurance[coinId];
  const holding = state.exchange?.holdings[coinId];
  if (!policy || now >= policy.expiresAt || !holding) return state;
  const amount = holding.amount * preCrashPrice * INSURANCE_CONFIG.payoutRatio;
  if (amount <= 0) return state;
  const insurance = { ...state.risk.insurance };
  delete insurance[coinId];
  return {
    ...state,
    coins: state.coins + amount,
    totalEarnedThisRun: state.totalEarnedThisRun + amount,
    risk: { ...state.risk, insurance, lastInsurancePayout: { coinId, amount, at: now } },
    statistics: { ...state.statistics, totalEarned: state.statistics.totalEarned + amount },
  };
}

/**
 * Start a rug pull on a meme coin (also used by the dev debug helper):
 * crash state + emergency news + insurance payout.
 */
export function beginRugPull(state: GameState, coinId: string, now: number): GameState {
  const cfg = getCoinConfig(coinId);
  const coin = state.exchange?.coins[coinId];
  const rug = state.risk?.rugs[coinId];
  if (!cfg || cfg.tier !== 'meme' || !coin || !rug || rug.phase !== 'none') return state;

  const drop = randRange(RUG_CONFIG.dropMin, RUG_CONFIG.dropMax);
  const nextRug: RugPullState = {
    ...rug,
    phase: 'crashing',
    startedAt: now,
    phaseEndsAt: now + randRange(RUG_CONFIG.crashMin, RUG_CONFIG.crashMax),
    startPrice: coin.price,
    targetPrice: Math.max(cfg.minPrice, coin.price * (1 - drop)),
    panicUsed: false,
  };
  let next: GameState = {
    ...state,
    risk: { ...state.risk, rugs: { ...state.risk.rugs, [coinId]: nextRug } },
  };
  next = publishRugNews(next, coinId, now);
  return payInsurance(next, coinId, coin.price, now);
}

/** Handle new pump-end events: roll the rug chance once per event. */
function detectPumpEnds(state: GameState, now: number): GameState {
  let next = state;
  for (const [coinId, rug] of Object.entries(state.risk.rugs)) {
    const whaleEnd = state.whales?.[coinId]?.pumpEndedAt ?? 0;
    const scenarioEnd = state.risk.scenarios[coinId]?.pumpEndedAt ?? 0;
    const event = Math.max(whaleEnd, scenarioEnd);
    if (event <= rug.lastPumpEventAt) continue;
    const marked = { ...next.risk.rugs[coinId], lastPumpEventAt: event };
    next = { ...next, risk: { ...next.risk, rugs: { ...next.risk.rugs, [coinId]: marked } } };
    if (marked.phase === 'none' && Math.random() < RUG_CONFIG.chance) {
      next = beginRugPull(next, coinId, now);
    }
  }
  return next;
}

const NEXT_RUG_PHASE = {
  crashing: { phase: 'floor', min: RUG_CONFIG.floorMin, max: RUG_CONFIG.floorMax },
  floor: { phase: 'recovering', min: RUG_CONFIG.recoverDuration, max: RUG_CONFIG.recoverDuration },
  recovering: { phase: 'none', min: 0, max: 0 },
} as const;

function advanceRugPhases(state: GameState, now: number): GameState {
  let rugs: RiskState['rugs'] | null = null;
  for (const [coinId, rug] of Object.entries(state.risk.rugs)) {
    if (rug.phase === 'none' || now < rug.phaseEndsAt) continue;
    const step = NEXT_RUG_PHASE[rug.phase];
    rugs = rugs ?? { ...state.risk.rugs };
    rugs[coinId] = { ...rug, phase: step.phase, phaseEndsAt: now + randRange(step.min, step.max) };
  }
  return rugs ? { ...state, risk: { ...state.risk, rugs } } : state;
}

/** Append current prices to the rolling sample window (2s cadence). */
function samplePrices(state: GameState, now: number): GameState {
  if (now - state.risk.lastSampleAt < PANIC_CONFIG.sampleInterval) return state;
  const samples: Record<string, PriceSample[]> = {};
  for (const coinId of Object.keys(state.risk.rugs)) {
    // Only sample listed coins (delisted / pool coins are frozen).
    if (state.exchange.listing[coinId]?.status !== 'listed') continue;
    const price = state.exchange.coins[coinId]?.price;
    if (price === undefined) continue;
    samples[coinId] = [...(state.risk.samples[coinId] ?? []), { t: now, p: price }]
      .slice(-PANIC_CONFIG.maxSamples);
  }
  return { ...state, risk: { ...state.risk, samples, lastSampleAt: now } };
}

export const rugPullSystem: TickSystem<GameState, GameAction> = {
  name: 'rugPull',

  update(state) {
    if (!state.isRunning || !state.risk || !state.exchange) return state;
    const now = Date.now();
    let next = samplePrices(state, now);
    next = detectPumpEnds(next, now);
    return advanceRugPhases(next, now);
  },
};
